import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Star, Quote, Users, Heart, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating = 5 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} color={i <= rating ? "#F5C518" : "#E2E8F0"} fill={i <= rating ? "#F5C518" : "none"} />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const isPadre = review.tipo === "padre";
  const accentColor = isPadre ? "#0072A4" : "#3CB44B";

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden"
      style={{ width: "min(300px, 82vw)" }}
      data-testid={`review-card-${review.id}`}
    >
      {/* Top accent bar */}
      <div className="h-1 w-full" style={{ background: accentColor }} />

      <div className="p-5">
        {/* Header row: quote icon + type badge */}
        <div className="flex items-center justify-between mb-3">
          {/* Quote icon — inline, never clipped */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${accentColor}15` }}
          >
            <Quote size={14} color={accentColor} />
          </div>
          {/* Type badge */}
          <span
            className="text-[10px] font-['Montserrat'] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap"
            style={{ background: `${accentColor}12`, color: accentColor }}
          >
            {isPadre ? "Padre/Madre" : "Participante"}
          </span>
        </div>

        {/* Stars */}
        <StarRating rating={review.rating || 5} />

        {/* Quote text */}
        <p className="text-[#475569] font-['Montserrat'] text-xs leading-relaxed italic mt-3 mb-4 line-clamp-4">
          "{review.texto}"
        </p>

        {/* Author */}
        <div className="flex items-center gap-2 pt-3 border-t border-[#F1F5F9]">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${accentColor}12` }}
          >
            {isPadre
              ? <Users size={12} color={accentColor} />
              : <Heart size={12} color={accentColor} />
            }
          </div>
          <span className="font-['Montserrat'] font-semibold text-[#0F172A] text-xs">
            {review.autor || (isPadre ? "Familia participante" : "Participante")}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [current, setCurrent] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(res => setReviews(res.data))
      .catch(() => {});
  }, []);

  const total = reviews.length;

  const goTo = useCallback((idx) => {
    const next = ((idx % total) + total) % total;
    setCurrent(next);
    const track = trackRef.current;
    if (!track) return;
    const cardW = (track.children[0]?.offsetWidth || 300) + 16;
    track.scrollTo({ left: cardW * next, behavior: "smooth" });
  }, [total]);

  // Auto-advance every 4.5s
  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(() => goTo(current + 1), 4500);
    return () => clearInterval(t);
  }, [current, total, goTo]);

  if (!reviews.length) return null;

  return (
    <section id="resenas" className="py-14 md:py-20 bg-gradient-to-b from-[#F0F8FF] to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#F5C518]/15 rounded-full px-5 py-2 mb-4">
            <Star size={14} color="#F5C518" fill="#F5C518" />
            <span className="text-[#0F172A] font-['Montserrat'] font-bold text-[10px] uppercase tracking-[0.18em]">Opiniones reales</span>
          </div>
          <h2
            className="font-['Montserrat'] font-extrabold text-[#0F172A] mb-3"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)", letterSpacing: "-0.01em", lineHeight: 1.2 }}
            data-testid="reviews-title"
          >
            Lo que dicen las familias
          </h2>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => <Star key={i} size={14} color="#F5C518" fill="#F5C518" />)}
            </div>
            <span className="font-['Montserrat'] font-bold text-sm text-[#0F172A] ml-1">5.0</span>
            <span className="text-[#94A3B8] font-['Montserrat'] text-xs">/ {total} reseñas</span>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between mb-5">
          <span className="font-['Montserrat'] text-xs text-[#94A3B8]">
            {current + 1} / {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => goTo(current - 1)}
              className="w-8 h-8 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center hover:border-[#0072A4] hover:bg-[#F0F8FF] transition-colors active:scale-90"
              aria-label="Anterior"
              data-testid="reviews-prev"
            >
              <ChevronLeft size={15} color="#475569" />
            </button>
            <button
              onClick={() => goTo(current + 1)}
              className="w-8 h-8 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center hover:border-[#0072A4] hover:bg-[#F0F8FF] transition-colors active:scale-90"
              aria-label="Siguiente"
              data-testid="reviews-next"
            >
              <ChevronRight size={15} color="#475569" />
            </button>
          </div>
        </div>

        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto pb-4"
          style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {reviews.map((review) => (
            <div key={review.id} style={{ scrollSnapAlign: "start", flexShrink: 0 }}>
              <ReviewCard review={review} />
            </div>
          ))}
        </div>

        {/* Dots */}
        {total > 1 && (
          <div className="flex gap-1.5 justify-center mt-5">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === current ? 20 : 6,
                  height: 6,
                  background: i === current ? "#0072A4" : "#E2E8F0",
                }}
                aria-label={`Ir a reseña ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
